import { ColumnType } from '@/Types/global';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import Table from '@/components/ui/table';
import { authState } from '@/vars/auth';
import { useQuery, useReactiveVar } from '@apollo/client';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';
import { Ellipsis } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GET_TRAINER_PAYMENT_SUMMARY } from './GraphQL/queries';

const PaymentSummary = () => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const { userDetails } = useReactiveVar(authState);
  const [filterValues, setFilterValues] = useState<{
    [key: string]: string | number | undefined;
  }>({
    page: 0,
  });
  const [localFilters, setLocalFilters] = useState<[Date | undefined, Date | undefined]>([
    undefined,
    undefined,
  ]);

  const handleDateFilterChange = (start: Date | undefined, end: Date | undefined) => {
    setLocalFilters([start, end]);
    setFilterValues((prev) => ({ ...prev, page: 0 }));
  };

  const { data, loading } = useQuery(GET_TRAINER_PAYMENT_SUMMARY, {
    variables: {
      search: search,
      filter: {
        trainerId: userDetails?._id,
        ...(localFilters?.[0] &&
          localFilters?.[1] && {
            paidOn: {
              $gte: dayjs(localFilters?.[0]).startOf('day').toISOString(),
              $lte: dayjs(localFilters?.[1]).endOf('day').toISOString(),
            },
          }),
      },
      sort: { paidOn: -1 },
      limit: 10,
      offset: Math.max(+(filterValues?.['page'] || 0) - 1, 0) * 10,
      trainerPaymentHistoryCountFilter2: {
        trainerId: userDetails?._id,
        ...(localFilters?.[0] &&
          localFilters?.[1] && {
            paidOn: {
              $gte: dayjs(localFilters?.[0]).startOf('day').toISOString(),
              $lte: dayjs(localFilters?.[1]).endOf('day').toISOString(),
            },
          }),
      },
      trainerPaymentHistoryCountSearch2: search,
    },
    fetchPolicy: 'network-only',
  });

  const handleNavigate = (id: string) => {
    navigate(`/payment/${encodeURIComponent(id)}`);
  };

  const Columns: ColumnType[] = [
    { title: 'Date', id: 'date', isVisible: true },
    { title: 'Transaction ID', id: 'transId', isVisible: true },
    { title: 'Session Count', id: 'note', isVisible: true },
    { title: 'Amount', id: 'amount', isVisible: true },
    { title: 'Action', id: 'action', isVisible: true },
  ];

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const tableData =
    data &&
    data?.trainerPaymentHistory &&
    data?.trainerPaymentHistory?.length > 0 &&
    data?.trainerPaymentHistory?.map((row) => ({
      onClick: () => handleNavigate(row?.transactionId || ''),
      cells: {
        id: row?.sessionUniqId,
        date: dayjs(row?.paidOn as string).format('MM/DD/YYYY'),
        transId: row?._id ?? '',
        note: <div className='pl-10'>{row?.sessionCount ?? ''}</div>,
        amount: <div className='pl-4'>{row?.amount ? `$${row.amount}` : ''}</div>,
        action: (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button id='trainer-btn' variant='ghost' size='icon' className='hover:bg-slate-200'>
                <Ellipsis />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className='cursor-pointer'
                  onClick={() => handleNavigate(row?.transactionId || '')}
                >
                  View
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    }));

  return (
    <div className='h-full w-full '>
      <div className='flex flex-col align-center '>
        <Table
          id={'paymetTable'}
          noPadding={false}
          loading={loading}
          columns={Columns}
          searchPlaceholder='Search by Transaction ID'
          onSearchChange={handleSearch}
          tableHeader
          setFilterValues={setFilterValues}
          filterValues={filterValues}
          data={tableData || []}
          itemsPerPage={10}
          dataCount={data?.trainerPaymentHistoryCount}
          onPageChange={(page) => setFilterValues({ page: page })}
          page={toNumber(filterValues?.page) || 0}
          startDate={localFilters[0]}
          endDate={localFilters[1]}
          onDateFilterChange={handleDateFilterChange}
          heading='You can see the amount credited to your account for the completed sessions.'
          text='Payment Summary'
        />
      </div>
    </div>
  );
};

export function LoadingComponent() {
  return (
    <div className='h-full w-full flex'>
      <div className='flex flex-row mt-12 gap-7 mx-auto'>
        <div className='flex flex-col max-w-[90%] w-[1000px]'>
          <Skeleton className='h-7 w-48 mb-2 bg-bgSecondary' />
          <Skeleton className='h-4 w-96 mb-6 bg-bgSecondary' />
          <LoadingTable />
        </div>
      </div>
    </div>
  );
}

function LoadingTable() {
  return (
    <div className='flex flex-col gap-4'>
      <Skeleton className='h-10  bg-bgSecondary' />
      {[...Array(5)].map((_, index) => (
        <Skeleton key={index} className='h-12 bg-bgSecondary' />
      ))}
    </div>
  );
}

export default PaymentSummary;
